import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getApiBaseUrl, API_CONFIG } from '../../constants/api.constants';

/**
 * API Response wrapper type
 */
export interface ApiResponse<T = any> {
    data: T;
    status: number;
    message?: string;
}

/**
 * API Error type
 */
export interface ApiError {
    message: string;
    status?: number;
    data?: any;
}

/**
 * API Client Configuration
 */
export interface ApiClientConfig {
    baseURL?: string;
    timeout?: number;
    headers?: Record<string, string>;
}

/**
 * ApiClient - A robust HTTP client class for making API requests
 *
 * Features:
 * - Request/Response interceptors
 * - Automatic error handling
 * - Authentication header management
 * - TypeScript generics for type-safe requests
 *
 * @example
 * const apiClient = new ApiClient({ baseURL: 'https://api.example.com' });
 * const response = await apiClient.get<User>('/users/1');
 */
class ApiClient {
    private axiosInstance: AxiosInstance;
    private authToken: string | null = null;

    /**
     * Creates an instance of ApiClient
     * @param config - Configuration options for the API client
     */
    constructor(config: ApiClientConfig = {}) {
        const baseURL = config.baseURL || import.meta.env.VITE_API_BASE_URL || getApiBaseUrl();

        const defaultConfig: AxiosRequestConfig = {
            baseURL,
            timeout: config.timeout || API_CONFIG.TIMEOUT,
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
                ...config.headers,
            },
        };

        // Log the base URL being used in development
        if (import.meta.env.DEV) {
            console.log('[API Client] Base URL:', baseURL);
            console.log('[API Client] Environment:', import.meta.env.MODE);
        }

        this.axiosInstance = axios.create(defaultConfig);
        this.setupInterceptors();
    }

    /**
     * Set up request and response interceptors
     */
    private setupInterceptors(): void {
        // Request interceptor
        this.axiosInstance.interceptors.request.use(
            (config) => {
                // Add authentication token if available
                if (this.authToken) {
                    config.headers.Authorization = `Bearer ${this.authToken}`;
                }

                // Log request in development
                if (import.meta.env.DEV) {
                    console.log(
                        `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
                        config.data
                    );
                }

                return config;
            },
            (error) => {
                console.error('[API Request Error]', error);
                return Promise.reject(error);
            }
        );

        // Response interceptor
        this.axiosInstance.interceptors.response.use(
            (response: AxiosResponse) => {
                // Log response in development
                if (import.meta.env.DEV) {
                    console.log(
                        `[API Response] ${response.config.method?.toUpperCase()} ${
                            response.config.url
                        }`,
                        response.data
                    );
                }

                return response;
            },
            (error: AxiosError) => {
                return this.handleError(error);
            }
        );
    }

    /**
     * Handle API errors
     */
    private handleError(error: AxiosError): Promise<never> {
        const apiError: ApiError = {
            message: 'An unexpected error occurred',
            status: error.response?.status,
            data: error.response?.data,
        };

        if (error.response) {
            // Server responded with error status
            const status = error.response.status;
            const data = error.response.data as Record<string, unknown> | undefined;
            const serverMessage =
                typeof data?.message === 'string' && data.message.trim()
                    ? data.message
                    : null;

            switch (status) {
                case 401:
                    apiError.message =
                        serverMessage ?? 'Unauthorized. Please log in again.';
                    // Clear auth token on 401
                    this.clearAuthToken();
                    break;
                case 403:
                    apiError.message =
                        serverMessage ??
                        'Forbidden. You do not have permission to access this resource.';
                    break;
                case 404:
                    apiError.message = serverMessage ?? 'Resource not found.';
                    break;
                case 500:
                    apiError.message =
                        serverMessage ?? 'Internal server error. Please try again later.';
                    break;
                default:
                    apiError.message =
                        serverMessage ?? `Request failed with status ${status}`;
            }
        } else if (error.request) {
            // Request made but no response received
            apiError.message = 'No response from server. Please check your internet connection.';
        }

        console.error('[API Error]', apiError);
        return Promise.reject(apiError);
    }

    /**
     * Set authentication token
     * @param token - JWT or other authentication token
     */
    public setAuthToken(token: string): void {
        this.authToken = token;
        // Optionally store in localStorage
        localStorage.setItem('authToken', token);
    }

    /**
     * Clear authentication token
     */
    public clearAuthToken(): void {
        this.authToken = null;
        localStorage.removeItem('authToken');
    }

    /**
     * Load authentication token from storage
     */
    public loadAuthToken(): void {
        const token = localStorage.getItem('authToken');
        if (token) {
            this.authToken = token;
        }
    }

    /**
     * GET request
     * @param url - API endpoint
     * @param config - Optional axios config
     * @returns Promise with typed response data
     */
    public async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.get<T>(url, config);
        return response.data;
    }

    /**
     * POST request
     * @param url - API endpoint
     * @param data - Request body
     * @param config - Optional axios config
     * @returns Promise with typed response data
     */
    public async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.post<T>(url, data, config);
        return response.data;
    }

    /**
     * PUT request
     * @param url - API endpoint
     * @param data - Request body
     * @param config - Optional axios config
     * @returns Promise with typed response data
     */
    public async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.put<T>(url, data, config);
        return response.data;
    }

    /**
     * PATCH request
     * @param url - API endpoint
     * @param data - Request body
     * @param config - Optional axios config
     * @returns Promise with typed response data
     */
    public async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.patch<T>(url, data, config);
        return response.data;
    }

    /**
     * DELETE request
     * @param url - API endpoint
     * @param config - Optional axios config
     * @returns Promise with typed response data
     */
    public async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.axiosInstance.delete<T>(url, config);
        return response.data;
    }

    /**
     * Get the underlying axios instance for advanced use cases
     */
    public getAxiosInstance(): AxiosInstance {
        return this.axiosInstance;
    }
}

// Create and export a singleton instance
const apiClient = new ApiClient();

// Load auth token on initialization
apiClient.loadAuthToken();

export default apiClient;

export function getApiErrorMessage(err: unknown): string {
    if (err instanceof Error && err.message) {
        return err.message;
    }
    if (err && typeof err === 'object' && 'message' in err) {
        const message = (err as { message?: unknown }).message;
        if (typeof message === 'string' && message.trim()) {
            return message;
        }
    }
    return 'An unexpected error occurred';
}
